/**
 * The employee's own record, in their own hands.
 *
 * Split by who owns each field rather than presented as one flat form, because
 * a form where half the boxes silently refuse to save is worse than one that
 * says which half is yours. Three sections, each labelled with what it is:
 *
 *  - yours to set, no approval
 *  - yours to correct, and the change is recorded
 *  - set by your company, shown for reference
 *
 * Read-only fields are rendered as text, not as disabled inputs. A greyed-out
 * box invites a click and then refuses it; a line of text does not pretend.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BellRing,
  CheckCircle2,
  Clock,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Save,
  SendHorizontal,
  ShieldCheck,
  KeyRound,
  Trash2,
  Upload,
  Volume2,
  VolumeX,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { client, errorMessage, setSignOutReason, tokenStore } from '../api/client';
import { useAuth } from '../store/auth';
import {
  notificationState,
  requestNotifications,
  setSoundEnabled,
  soundEnabled,
} from '../lib/checkInAlarm';
import type {
  BloodGroup,
  Certificate,
  MyProfile,
  Page,
  ProfileReviewStatus,
  StoredDocument,
} from '../types';

/** The eight real groups. Absent means not provided — there is no "unknown". */
const BLOOD_GROUPS: BloodGroup[] = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-40 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-sm text-gray-900">
        {value || <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/**
 * The one place the review state is explained to the employee.
 *
 * Four states, four messages. The submit button lives here rather than beside
 * the Save button because submitting is a different act from saving — it hands
 * the whole record over and freezes it — and the states where it makes sense
 * (draft, rejected) are exactly the ones this banner already knows.
 */
function ReviewStatusBanner({
  status,
  note,
  canSubmit,
  submitting,
  onSubmit,
}: {
  status: ProfileReviewStatus;
  note: string | null;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const SubmitButton = (
    <button
      type="button"
      className="btn-primary shrink-0 text-xs"
      disabled={submitting}
      onClick={onSubmit}
    >
      {submitting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <SendHorizontal className="h-3.5 w-3.5" />
      )}
      Submit for review
    </button>
  );

  if (status === 'approved') {
    return (
      <section className="card flex items-start gap-3 border-l-4 border-l-emerald-500">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Approved and locked</p>
          <p className="mt-0.5 text-xs text-gray-600">
            Your details and documents have been approved and can no longer be
            changed here. If something genuinely needs to change — a new bank
            account, say — ask your company admin to unlock your profile.
          </p>
        </div>
      </section>
    );
  }

  if (status === 'pending') {
    return (
      <section className="card flex items-start gap-3 border-l-4 border-l-amber-500">
        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Under review</p>
          <p className="mt-0.5 text-xs text-gray-600">
            Your profile has been submitted and is locked while an administrator
            reviews it. You’ll be able to make changes again if it’s sent back.
          </p>
        </div>
      </section>
    );
  }

  if (status === 'rejected') {
    return (
      <section className="card border-l-4 border-l-rose-500">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">Sent back for changes</p>
            {note ? (
              <p className="mt-0.5 whitespace-pre-wrap text-xs text-gray-700">
                <span className="font-medium">Reason:</span> {note}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-gray-600">
                Please review your details and submit again.
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Update the flagged details and documents below, then submit again.
            </p>
          </div>
          {canSubmit && <div className="ml-1">{SubmitButton}</div>}
        </div>
      </section>
    );
  }

  // draft
  return (
    <section className="card flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">Not yet submitted</p>
        <p className="mt-0.5 text-xs text-gray-600">
          Fill in your details and upload your documents below. When everything
          is complete, submit your profile for review. It will be locked while
          an administrator checks it.
        </p>
      </div>
      {canSubmit && SubmitButton}
    </section>
  );
}

export default function MyProfilePage() {
  const queryClient = useQueryClient();

  const profile = useQuery<MyProfile>({
    queryKey: ['my-profile'],
    queryFn: async () => (await client.get('/api/v1/employees/me/profile')).data,
    retry: false,
  });

  const [form, setForm] = useState({
    bloodGroup: '',
    emergencyContact: '',
    personalEmail: '',
    bankName: '',
    bankIfsc: '',
    bankAccountNumber: '',
    confirmAccountNumber: '',
    pan: '',
    aadhaarNumber: '',
  });

  // Seeded once the record arrives. Without this the boxes render empty and a
  // save would clear fields the employee never touched.
  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    setForm({
      bloodGroup: p.bloodGroup ?? '',
      emergencyContact: p.emergencyContact ?? '',
      personalEmail: p.personalEmail ?? '',
      bankName: p.bankName ?? '',
      bankIfsc: p.bankIfsc ?? '',
      // Deliberately not seeded. The stored number never reaches the browser,
      // so there is nothing to seed them with — and an empty pair reads
      // correctly as "type a new one to change it" rather than as a value
      // somebody could accidentally save back.
      bankAccountNumber: '',
      confirmAccountNumber: '',
      // Sensitive identifiers are never seeded in full. The server returns
      // only masked values; the employee enters a new full value to change it.
      pan: '',
      aadhaarNumber: '',
    });
  }, [profile.data]);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.patch('/api/v1/employees/me/profile', body)).data as MyProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['my-profile'], data);
      toast.success('Profile saved');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save your profile')),
  });

  const submitForReview = useMutation({
    mutationFn: async () =>
      (await client.post('/api/v1/employees/me/profile/submit')).data as MyProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['my-profile'], data);
      toast.success('Submitted for review');
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not submit your profile for review')),
  });

  if (profile.isLoading) {
    return (
      <div className="flex justify-center py-12 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (profile.isError || !profile.data) {
    /**
     * Two very different reasons land here, and conflating them was a bug.
     *
     * A 404 is ordinary: company admins and owners are routinely created
     * without being onboarded, so there is genuinely no employee record and
     * saying so is correct.
     *
     * Anything else is a failure — the server 500ing, the network gone — and
     * this branch used to report it as "no employee record is linked to your
     * account" regardless. That is the product asserting an administrative
     * fact about somebody's employment on the strength of a failed request,
     * and every field on the page hangs off this data, so the whole record
     * appeared to have been wiped. It reads as data loss rather than as an
     * outage, which is the wrong alarm entirely.
     */
    const status = axios.isAxiosError(profile.error)
      ? profile.error.response?.status
      : undefined;
    // Loaded-but-empty counts as "no record"; only a real error can be a fault.
    const noRecord = !profile.isError || status === 404;

    return (
      <div className="space-y-6">
        {noRecord ? (
          <div className="card">
            <p className="text-sm text-gray-600">
              No employee record is linked to your account, so there is no
              profile to show. Your company admin can create one.
            </p>
          </div>
        ) : (
          <div className="card border-l-4 border-l-rose-500">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  Could not load your profile
                </p>
                {/* The server's own message, not a generic one. When this is a
                    missing column behind a 500 it is the only clue anybody
                    gets, and swallowing it is how one broken endpoint becomes
                    an afternoon of guessing. */}
                <p className="mt-0.5 text-xs text-gray-600">
                  {errorMessage(profile.error, 'The server did not respond.')}
                  {status ? ` (HTTP ${status})` : ''}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Your details have not been lost — they could not be fetched.
                  Try again, and tell your administrator if it keeps happening.
                </p>
                <button
                  type="button"
                  className="btn-secondary mt-2 text-xs"
                  onClick={() => void profile.refetch()}
                  disabled={profile.isFetching}
                >
                  {profile.isFetching ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Offered either way. Every account has a password whether or not it
            has an employee record, and the accounts that lack one — company
            admins and owners — are the ones whose passwords matter most.
            Deliberately not hidden on the error branch either: changing a
            password does not depend on the profile having loaded. */}
        <ChangePasswordSection />
      </div>
    );
  }

  const p = profile.data;

  // PENDING and APPROVED freeze every self-service write — the same rule the
  // server enforces (409). The form and the document controls read this to
  // stop offering an edit that would only be refused, and the banner explains
  // why. DRAFT and REJECTED are editable.
  const locked = p.profileStatus === 'pending' || p.profileStatus === 'approved';
  const canSubmit = p.profileStatus === 'draft' || p.profileStatus === 'rejected';

  /**
   * Client-side only, and never sent.
   *
   * The confirmation exists to catch a typo at entry, which is worth doing
   * because a mistyped account number is not discovered until a salary lands
   * somewhere else. It is not stored: two copies of an account number would
   * be two things to keep in step, twice the surface to encrypt, and twice as
   * much to scrub for a DPDP erasure.
   *
   * Only checked once something has been typed, so somebody editing their
   * bank name is not stopped by an empty pair of boxes.
   */
  const accountMismatch =
    Boolean(form.bankAccountNumber || form.confirmAccountNumber) &&
    form.bankAccountNumber.trim() !== form.confirmAccountNumber.trim();

  const aadhaarInvalid = Boolean(form.aadhaarNumber) && !/^\d{12}$/.test(form.aadhaarNumber.trim());

  const submit = () => {
    if (accountMismatch) return;
    if (aadhaarInvalid) {
      toast.error('Aadhaar number must contain exactly 12 digits');
      return;
    }
    save.mutate({
      // '' from an empty select means "not provided", which the API spells null.
      bloodGroup: form.bloodGroup || null,
      emergencyContact: form.emergencyContact.trim() || null,
      personalEmail: form.personalEmail.trim() || null,
      bankName: form.bankName.trim() || null,
      bankIfsc: form.bankIfsc.trim() || null,
      // Only when it was actually typed. Sending '' on every save would clear
      // a stored account number for anybody who edited their bank name.
      ...(form.bankAccountNumber
        ? { bankAccountNumber: form.bankAccountNumber.trim() }
        : {}),
      pan: form.pan.trim().toUpperCase() || null,
      // The full Aadhaar value is sent only when the employee explicitly enters
      // it. The API must encrypt it before persistence and must never return it.
      ...(form.aadhaarNumber.trim()
        ? { aadhaarNumber: form.aadhaarNumber.trim() }
        : {}),
    });
  };

  return (
    <div className="space-y-4">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">{p.fullName}</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          {p.designation ?? p.employeeCode}
          {p.department ? ` · ${p.department}` : ''}
        </p>
        {p.anonymisedAt && (
          <p className="mt-2 rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            Personal data on this record was erased on {p.anonymisedAt.slice(0, 10)}.
          </p>
        )}
      </section>

      <ReviewStatusBanner
        status={p.profileStatus}
        note={p.profileDecisionNote}
        canSubmit={canSubmit}
        submitting={submitForReview.isPending}
        onSubmit={() => submitForReview.mutate()}
      />

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* One disabled fieldset freezes every control inside at once while the
            profile is under review or locked — cleaner and harder to miss than
            a disabled prop on each input, and the server refuses the write
            regardless. */}
        <fieldset disabled={locked} className="m-0 min-w-0 space-y-4 border-0 p-0">
        <section className="card space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Yours to set</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              None of these are required, and nobody has to approve them.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              Blood group
              <select
                className="input mt-1"
                value={form.bloodGroup}
                onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })}
              >
                <option value="">Not provided</option>
                {BLOOD_GROUPS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </label>

            <label className="text-xs text-gray-600">
              Emergency contact
              <input
                spellCheck={false}
                className="input mt-1"
                value={form.emergencyContact}
                onChange={(e) => setForm({ ...form, emergencyContact: e.target.value })}
                maxLength={200}
                placeholder="Name and number"
              />
            </label>

            <label className="text-xs text-gray-600 sm:col-span-2">
              Personal email
              <input
                spellCheck={false}
                className="input mt-1"
                type="email"
                value={form.personalEmail}
                onChange={(e) => setForm({ ...form, personalEmail: e.target.value })}
                placeholder="Where to reach you outside work"
              />
            </label>
          </div>
        </section>

        <section className="card space-y-3">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              Yours to correct
            </h3>
            {/* Said plainly rather than hidden: a recorded change is a fair
                thing to know about before making it. */}
            <p className="mt-0.5 text-xs text-gray-500">
              You can change these yourself, and each change is recorded for
              your company admin.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              Bank name
              <input
                spellCheck={false}
                className="input mt-1"
                value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                maxLength={120}
              />
            </label>

            <label className="text-xs text-gray-600">
              IFSC
              <input
                spellCheck={false}
                className="input mt-1 uppercase"
                value={form.bankIfsc}
                onChange={(e) => setForm({ ...form, bankIfsc: e.target.value })}
                maxLength={15}
                placeholder="HDFC0001234"
              />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              Account number
              {/* type=password rather than text: this is read out loud in
                  open-plan offices and screen-shared. It is a shoulder-surfing
                  measure and nothing more — the value still travels and is
                  still stored like any other field. */}
              <input
                type="password"
                spellCheck={false}
                autoComplete="off"
                className="input mt-1 font-mono"
                inputMode="numeric"
                value={form.bankAccountNumber}
                onChange={(e) =>
                  setForm({ ...form, bankAccountNumber: e.target.value })
                }
                maxLength={40}
                placeholder={p.bankAccountNumberMasked ?? 'Not set'}
              />
              {/* The tail of what is stored, so somebody can tell whether the
                  account on file is the right one without it being sent to
                  the browser in full. */}
              {p.bankAccountNumberMasked && !form.bankAccountNumber && (
                <span className="mt-1 block text-[11px] text-gray-400">
                  On file: {p.bankAccountNumberMasked}. Type a new one to
                  change it.
                </span>
              )}
            </label>

            <label className="text-xs text-gray-600">
              Confirm account number
              <input
                type="password"
                spellCheck={false}
                autoComplete="off"
                className="input mt-1 font-mono"
                inputMode="numeric"
                value={form.confirmAccountNumber}
                onChange={(e) =>
                  setForm({ ...form, confirmAccountNumber: e.target.value })
                }
                maxLength={40}
              />
            </label>
          </div>

          {accountMismatch && (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
              The two account numbers do not match.
            </p>
          )}

          <p className="text-[11px] text-gray-400">
            Digits only, 9 to 18 of them. The confirmation is checked here and
            never stored — one account number is kept, not two.
          </p>
        </section>


        <section className="card space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Statutory identifiers
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Used for payroll and statutory filings. Changes are audited.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              PAN
              <input
                spellCheck={false}
                autoComplete="off"
                className="input mt-1 font-mono uppercase"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value })}
                maxLength={10}
                placeholder={p.panMasked ?? 'ABCDE1234F'}
              />
              {p.panMasked && !form.pan && (
                <span className="mt-1 block text-[11px] text-gray-400">
                  On file: {p.panMasked}. Type a new one to change it.
                </span>
              )}
            </label>

            <label className="text-xs text-gray-600">
              Aadhaar Number
              <input
                spellCheck={false}
                autoComplete="off"
                type="password"
                className="input mt-1 font-mono"
                inputMode="numeric"
                value={form.aadhaarNumber}
                onChange={(e) =>
                  setForm({ ...form, aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12) })
                }
                maxLength={12}
                placeholder={p.aadhaarLast4 ? `XXXXXXXX${p.aadhaarLast4}` : '12-digit Aadhaar number'}
                aria-invalid={aadhaarInvalid}
              />
              {aadhaarInvalid && (
                <span className="mt-1 block text-[11px] text-red-600">
                  Aadhaar number must contain exactly 12 digits.
                </span>
              )}
              {p.aadhaarLast4 && !form.aadhaarNumber && (
                <span className="mt-1 block text-[11px] text-gray-400">
                  On file: XXXXXXXX{p.aadhaarLast4}. Type a new 12-digit number to change it.
                </span>
              )}
            </label>
          </div>

          {/* Said plainly, because a box labelled "last 4" invites somebody to
              paste all twelve and wonder why it was refused. The server
              refuses that specifically rather than truncating it. */}
          <p className="text-[11px] text-gray-400">
            Enter the complete 12-digit Aadhaar number when changing it. The
            stored value should be encrypted server-side and only the last four
            digits should be returned to the browser.
          </p>
        </section>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="btn-primary"
            disabled={save.isPending || accountMismatch || aadhaarInvalid}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
        </fieldset>
      </form>

      <ProfilePhotoSection employeeId={p.id} locked={locked} />
      <IdentityDocumentsSection employeeId={p.id} locked={locked} />
      <CertificatesSection employeeId={p.id} locked={locked} />

      {/* Deliberately outside the review lock. These are browser settings,
          not part of the record an approver signs off, so freezing them with
          the profile would strand somebody muted for as long as their review
          took. */}
      <ChangePasswordSection />
      <CheckInAlertsSection />

      <section className="card">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <Lock className="h-4 w-4 text-gray-400" />
          Set by your company
        </h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Shown for reference. Ask your company admin if any of it is wrong.
        </p>
        {/* Text, not disabled inputs — a greyed box invites a click and then
            refuses it. */}
        <dl className="mt-2 divide-y divide-gray-100">
          <Field label="Employee code" value={p.employeeCode} />
          <Field label="Designation" value={p.designation} />
          <Field label="Department" value={p.department} />
          <Field label="Reports to" value={p.reportsToName} />
          <Field label="Joined" value={p.dateOfJoining} />
          {p.dateOfExit && <Field label="Left" value={p.dateOfExit} />}
          <Field label="Status" value={p.status.replace(/_/g, ' ')} />
          <Field label="Shift" value={p.shiftPolicyName} />
          <Field label="Workplace" value={p.officeName} />
          <Field label="Work email" value={p.email} />
        </dl>
      </section>
    </div>
  );
}

/**
 * Change your own password.
 *
 * Posts to the endpoint that already existed — /auth/change-password — which
 * had no caller anywhere in the app. Everything enforced below is enforced
 * there too; none of it is this form's own rule. What the form adds is the
 * confirmation box, which is deliberately client-side only: the server has no
 * use for a second copy of the same string, and sending one would put the new
 * password on the wire twice.
 *
 * Succeeding signs you out. That is the single-session rule applied
 * consistently — changing a password bumps token_version, and the caller's own
 * access token is minted under the old one. Said before the button is pressed
 * rather than discovered afterwards, and the reason is handed to the login
 * screen so the redirect explains itself.
 */
function ChangePasswordSection() {
  // From the auth store, not from MyProfile: must_change_password is a fact
  // about the login, and MyProfile describes the employee record. Plenty of
  // accounts have one and not the other.
  const mustChange = useAuth((s) => s.user?.mustChangePassword ?? false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Checked here only to keep the mismatch out of a round trip. Everything
  // that actually matters — the current password, the length, the character
  // mix, the difference from the old one — is the server's call.
  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = current.length > 0 && next.length > 0 && !mismatch;

  const change = useMutation({
    mutationFn: async () => {
      await client.post('/api/v1/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      });
    },
    onSuccess: () => {
      toast.success('Password updated');
      setSignOutReason(
        'Your password was changed, so every session was signed out — '
        + 'including this one. Sign in with your new password.',
      );
      tokenStore.clear();
      // A hard navigation rather than a router push: every query in the app is
      // now holding a dead token, and letting React re-render first sets off a
      // wave of 401s before the redirect lands.
      window.location.href = `${import.meta.env.BASE_URL || '/'}login`.replace('//', '/');
    },
    onError: (e) => setError(errorMessage(e, 'Could not change the password')),
  });

  return (
    <section className="card">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <KeyRound className="h-4 w-4 text-gray-400" />
        Password
      </h3>

      {/* The first-login prompt is satisfied by this form, so it says so
          here rather than leaving somebody hunting for the right screen. */}
      {mustChange ? (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
          You are still on the password your administrator issued. Setting your
          own here clears that.
        </p>
      ) : (
        <p className="mt-0.5 text-xs text-gray-500">
          Changing this signs out every device, including this one.
        </p>
      )}

      <form
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          change.mutate();
        }}
      >
        <label className="block text-xs text-gray-600">
          Current password
          <input
            type="password"
            autoComplete="current-password"
            className="input mt-1"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </label>

        <label className="block text-xs text-gray-600">
          New password
          <input
            type="password"
            autoComplete="new-password"
            className="input mt-1"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            At least 10 characters, mixing letters with numbers or symbols.
          </span>
        </label>

        <label className="block text-xs text-gray-600">
          Confirm new password
          <input
            type="password"
            autoComplete="new-password"
            className="input mt-1"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {mismatch && (
            <span className="mt-0.5 block text-[11px] text-red-600">
              These two do not match.
            </span>
          )}
        </label>

        {error && (
          <p
            className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
            role="alert"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={!ready || change.isPending}
        >
          {change.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <KeyRound className="h-4 w-4" />
          )}
          Change password
        </button>
      </form>
    </section>
  );
}

/**
 * Check-in sound and desktop alerts.
 *
 * Moved here from the shift bar, which is the wrong home for a preference: it
 * put two standing settings in front of somebody at the one moment they were
 * trying to start work, every single day. This is where a setting belongs, and
 * it is reachable whether or not a shift is running.
 *
 * Nothing here touches the server, which is why it sits apart from the rest of
 * this page and says so on screen. Sound is a localStorage flag and the
 * notification grant belongs to the browser, so both are per-device: setting
 * them on a laptop says nothing about the same person's tablet. Stating that
 * is the difference between a setting somebody can rely on and one that
 * appears not to have saved.
 *
 * Both settings exist for the same reason — a check-in nobody notices is
 * scored as missed, and that costs an availability score — so they are one
 * section rather than two.
 */
function CheckInAlertsSection() {
  const [sound, setSound] = useState(soundEnabled);
  const [notify, setNotify] = useState(notificationState);

  return (
    <section className="card">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <BellRing className="h-4 w-4 text-gray-400" />
        Check-in alerts
      </h3>
      <p className="mt-0.5 text-xs text-gray-500">
        Saved in this browser only, not on your record — set them again on any
        other computer you sign in from.
      </p>

      <div className="mt-3 divide-y divide-gray-100">
        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="text-sm text-gray-900">Sound</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {sound
                ? 'Check-ins play a short tone.'
                : 'Check-ins are silent. You will only see them if this tab is in front of you.'}
            </p>
          </div>
          <button
            type="button"
            className={sound ? 'btn-secondary shrink-0 text-xs' : 'btn-primary shrink-0 text-xs'}
            onClick={() => {
              const next = !sound;
              setSoundEnabled(next);
              setSound(next);
            }}
          >
            {sound ? (
              <VolumeX className="h-3.5 w-3.5" />
            ) : (
              <Volume2 className="h-3.5 w-3.5" />
            )}
            {sound ? 'Turn off' : 'Turn on'}
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="text-sm text-gray-900">Desktop alerts</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {notify === 'granted'
                && 'On. You will be alerted even when this tab is in the background.'}
              {notify === 'default'
                && 'Off. Without these, a check-in that arrives while you are in another tab goes unnoticed.'}
              {/* Permanent from here — the browser will not prompt again — so
                  this says where to change it rather than offering a button
                  that would do nothing. */}
              {notify === 'denied'
                && 'Blocked. Re-enable notifications for this site in your browser settings.'}
              {notify === 'unsupported'
                && 'This browser cannot show desktop notifications.'}
            </p>
          </div>
          {/* Asked from a click, because a request made on page load is
              ignored or held against the origin. */}
          {notify === 'default' && (
            <button
              type="button"
              className="btn-primary shrink-0 text-xs"
              onClick={async () => setNotify(await requestNotifications())}
            >
              <BellRing className="h-3.5 w-3.5" />
              Turn on
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/** Images only, and the set the storage layer already accepts. */
const PHOTO_ACCEPT = '.jpg,.jpeg,.png,.webp';
/** PDFs and images, matching what the documents panel offers elsewhere. */
const CERT_ACCEPT = '.pdf,.jpg,.jpeg,.png';
/** The server's own cap. Checked here to save a round trip to be refused. */
const MAX_BYTES = 10 * 1024 * 1024;

function tooBig(file: File): boolean {
  return file.size > MAX_BYTES;
}

/**
 * Uploading through the ordinary documents endpoint.
 *
 * Not a second storage path: the same POST /documents every other screen
 * uses, which is what the narrow self-service rule was cut to allow. It
 * accepts profile_photo and education_certificate on the caller's own record
 * and refuses everything else, so nothing here needs its own permission
 * check — the endpoint is the check.
 */
async function uploadDocument(
  employeeId: string,
  docType: 'profile_photo' | 'education_certificate' | 'aadhaar_front' | 'aadhaar_back' | 'pan_card',
  file: File,
  certificateId?: string,
): Promise<StoredDocument> {
  const body = new FormData();
  // Snake_case by hand: the request interceptor skips FormData, matching how
  // DocumentsPanel builds the same call.
  body.append('doc_type', docType);
  body.append('employee_id', employeeId);
  if (certificateId) body.append('certificate_id', certificateId);
  body.append('file', file);
  return (await client.post('/api/v1/documents', body)).data;
}

/**
 * Identity documents are ordinary Documents. The backend must authorize these
 * types only on the caller's own employee record.
 */
const IDENTITY_DOCS = [
  { key: 'aadhaar_front', label: 'Aadhaar Front', accept: CERT_ACCEPT },
  { key: 'aadhaar_back', label: 'Aadhaar Back', accept: CERT_ACCEPT },
  { key: 'pan_card', label: 'PAN Card', accept: CERT_ACCEPT },
] as const;

function IdentityDocumentsSection({
  employeeId,
  locked,
}: {
  employeeId: string;
  locked: boolean;
}) {
  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Identity Documents</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Upload your Aadhaar front/back and PAN card securely. Only you and users
          with the existing authorized document permissions can access them.
        </p>
      </div>
      <div className="space-y-2">
        {IDENTITY_DOCS.map(({ key, label, accept }) => (
          <IdentityDocumentRow
            key={key}
            employeeId={employeeId}
            docType={key}
            label={label}
            accept={accept}
            locked={locked}
          />
        ))}
      </div>
    </section>
  );
}

function IdentityDocumentRow({
  employeeId,
  docType,
  label,
  accept,
  locked,
}: {
  employeeId: string;
  docType: typeof IDENTITY_DOCS[number]['key'];
  label: string;
  accept: string;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery<Page<StoredDocument>>({
    queryKey: ['my-documents', docType],
    queryFn: async () =>
      (await client.get('/api/v1/documents', { params: { employeeId, docType } })).data,
  });

  const storedDocument = (data?.items ?? [])
    .slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

  const choose = async (file: File | undefined) => {
    if (!file) return;
    if (tooBig(file)) {
      toast.error('That file is over 10 MB');
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    // Match the server allowlist (storage.ALLOWED_TYPES): PDF/JPEG/PNG only.
    // WebP passes no server check, so accepting it here only earns a 422.
    const isImage = ['image/jpeg', 'image/png'].includes(file.type);
    if (!isPdf && !isImage) {
      toast.error('Use a PDF, JPG or PNG file');
      return;
    }
    setBusy(true);
    try {
      const existing = await client.get('/api/v1/documents', {
        params: { employeeId, docType },
      });
      const items = (existing.data as Page<StoredDocument>).items ?? [];
      await uploadDocument(employeeId, docType, file);
      for (const old of items) {
        await client.delete(`/api/v1/documents/${old.id}`);
      }
      await queryClient.invalidateQueries({ queryKey: ['my-documents', docType] });
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(errorMessage(e, `Could not upload ${label}`));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!storedDocument) return;
    setBusy(true);
    try {
      await client.delete(`/api/v1/documents/${storedDocument.id}`);
      await queryClient.invalidateQueries({ queryKey: ['my-documents', docType] });
      toast.success(`${label} removed`);
    } catch (e) {
      toast.error(errorMessage(e, `Could not remove ${label}`));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!storedDocument) return;
    try {
      const response = await client.get(`/api/v1/documents/${storedDocument.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = storedDocument.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMessage(e, `Could not download ${label}`));
    }
  };

  return (
    <div className="rounded-xl p-3 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {storedDocument ? (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              Uploaded: {storedDocument.filename}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-gray-400">Not uploaded</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {storedDocument && (
            <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => void download()}>
              View / Download
            </button>
          )}
          {storedDocument && !locked && (
            <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => void remove()}>
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
          {!locked && (
            <label className="btn-secondary inline-flex cursor-pointer text-xs">
              <Upload className="h-3.5 w-3.5" />
              {storedDocument ? 'Replace' : 'Upload'}
              <input
                type="file"
                className="hidden"
                accept={accept}
                disabled={busy}
                onChange={(e) => {
                  void choose(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}
          {locked && !storedDocument && (
            <span className="text-xs text-gray-400">Not uploaded</span>
          )}
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-500" />}
        </div>
      </div>
    </div>
  );
}

/**
 * The passport photo.
 *
 * Replace is an upload followed by a delete of the old one rather than an
 * edit: documents are immutable once stored, and the audit trail reads more
 * honestly as "one uploaded, one deleted" than as a mutation with no record
 * of what was there before.
 */
function ProfilePhotoSection({
  employeeId,
  locked,
}: {
  employeeId: string;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery<Page<StoredDocument>>({
    queryKey: ['my-documents', 'profile_photo'],
    queryFn: async () =>
      (
        await client.get('/api/v1/documents', {
          params: { employeeId, docType: 'profile_photo' },
        })
      ).data,
  });

  // Newest wins. Replace deletes the old one, but a failure between the two
  // would leave both, and the recent one is what somebody meant.
  const photo = (data?.items ?? [])
    .slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return;
    }
    let stale = false;
    let url: string | null = null;
    void client
      .get(`/api/v1/documents/${photo.id}/download`, { responseType: 'blob' })
      .then((response) => {
        if (stale) return;
        url = URL.createObjectURL(response.data as Blob);
        setPreview(url);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo?.id]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['my-documents', 'profile_photo'] });

  const choose = async (file: File | undefined) => {
    if (!file) return;
    if (tooBig(file)) {
      toast.error('That image is over 10 MB');
      return;
    }
    setBusy(true);
    try {
      const previous = photo?.id;
      await uploadDocument(employeeId, 'profile_photo', file);
      // Only after the new one is safely stored. Deleting first would lose
      // the existing photo if the upload then failed.
      if (previous) {
        await client.delete(`/api/v1/documents/${previous}`);
      }
      await refresh();
      toast.success('Photo updated');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not upload that photo'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!photo) return;
    setBusy(true);
    try {
      await client.delete(`/api/v1/documents/${photo.id}`);
      await refresh();
      toast.success('Photo removed');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not remove that photo'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Profile photo</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          A passport-style photograph. JPG, PNG or WebP, up to 10 MB.
        </p>
      </div>

      <div className="flex items-center gap-4">
        {/* Square, because a passport photo is portrait and cropping to a
            circle would cut the top of somebody's head off. */}
        {preview ? (
          <img
            src={preview}
            alt=""
            className="h-28 w-28 rounded-lg object-cover ring-1 ring-gray-200"
          />
        ) : (
          <div className="flex h-28 w-28 items-center justify-center rounded-lg bg-gray-50 text-xs text-gray-400 ring-1 ring-gray-200">
            None yet
          </div>
        )}

        <div className="space-y-2">
          {!locked && (
            <label className="btn-secondary inline-flex cursor-pointer text-xs">
              <Upload className="h-3.5 w-3.5" />
              {photo ? 'Replace' : 'Upload'}
              <input
                type="file"
                className="hidden"
                accept={PHOTO_ACCEPT}
                disabled={busy}
                onChange={(e) => {
                  void choose(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}
          {photo && !locked && (
            <button
              type="button"
              className="btn-ghost block text-xs"
              disabled={busy}
              onClick={() => void remove()}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
          {locked && !photo && (
            <p className="text-xs text-gray-400">No photo on file.</p>
          )}
          {busy && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Working…
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The draft a new certificate is built from.
 *
 * No issue or expiry date: those columns still exist on the table and the
 * server still refuses an expiry before an issue, but nothing on this form
 * sets them. Taking the boxes away is reversible in an afternoon; dropping
 * the columns would be a migration to get back, and rows imported or created
 * elsewhere may already carry dates the list below still displays.
 */
const EMPTY_CERT = {
  name: '',
  certificateType: '',
  issuingOrganisation: '',
  certificateNumber: '',
};

/**
 * Qualifications, each with an optional scan.
 *
 * A certificate is saved when it is added rather than on the page's Save
 * button. It is its own record with its own endpoint, and folding it into a
 * form that PATCHes five profile fields would mean one button doing two
 * unrelated things — and a failure in either half leaving the other
 * half-applied.
 */
function CertificatesSection({
  employeeId,
  locked,
}: {
  employeeId: string;
  locked: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(EMPTY_CERT);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data: certificates } = useQuery<Certificate[]>({
    queryKey: ['my-certificates'],
    queryFn: async () =>
      (await client.get('/api/v1/employees/me/certificates')).data,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['my-certificates'] });

  const add = useMutation({
    mutationFn: async () =>
      (
        await client.post('/api/v1/employees/me/certificates', {
          name: draft.name.trim(),
          certificateType: draft.certificateType.trim() || null,
          issuingOrganisation: draft.issuingOrganisation.trim() || null,
          certificateNumber: draft.certificateNumber.trim() || null,
        })
      ).data,
    onSuccess: () => {
      setDraft(EMPTY_CERT);
      setAdding(false);
      void refresh();
      toast.success('Certificate added');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not add that certificate')),
  });

  /**
   * Adding a scan, not replacing one.
   *
   * A certificate holds several now, so Replace has stopped being a coherent
   * action — replace which of them? Files are added and removed
   * individually instead, and the upload carries the certificate id so
   * attaching is one request that either works or does not. The two-step
   * version left an orphan file on the record whenever the second call
   * failed.
   */
  const attach = async (certificate: Certificate, file: File | undefined) => {
    if (!file) return;
    if (tooBig(file)) {
      toast.error('That file is over 10 MB');
      return;
    }
    setBusyId(certificate.id);
    try {
      await uploadDocument(
        employeeId,
        'education_certificate',
        file,
        certificate.id,
      );
      await refresh();
      toast.success('Scan added');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not attach that document'));
    } finally {
      setBusyId(null);
    }
  };

  /** One scan, without touching the claim it belonged to. */
  const removeScan = async (certificate: Certificate, scanId: string) => {
    setBusyId(certificate.id);
    try {
      await client.delete(`/api/v1/documents/${scanId}`);
      await refresh();
      toast.success('Scan removed');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not remove that scan'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (certificate: Certificate) => {
    setBusyId(certificate.id);
    try {
      await client.delete(`/api/v1/employees/me/certificates/${certificate.id}`);
      // The scans go with it here, deliberately: the server leaves them alone
      // so that removing a claim never silently deletes evidence, and this is
      // the one place that knows the person meant both.
      for (const scan of certificate.scans) {
        await client.delete(`/api/v1/documents/${scan.id}`);
      }
      await refresh();
      toast.success('Certificate removed');
    } catch (e) {
      toast.error(errorMessage(e, 'Could not remove that certificate'));
    } finally {
      setBusyId(null);
    }
  };

  const download = async (scan: { id: string; filename: string }) => {
    try {
      const response = await client.get(
        `/api/v1/documents/${scan.id}/download`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = scan.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not download that file'));
    }
  };

  return (
    <section className="card space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Certificates and qualifications
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Yours to record. Only the name is required — the rest can follow
            when you have it.
          </p>
        </div>
        {!adding && !locked && (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add certificate
          </button>
        )}
      </div>

      {adding && !locked && (
        <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-600">
              Certificate name
              <input
                className="input mt-1"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                maxLength={200}
                placeholder="B.Tech Computer Science"
              />
            </label>
            <label className="text-xs text-gray-600">
              Type
              <input
                className="input mt-1"
                value={draft.certificateType}
                onChange={(e) =>
                  setDraft({ ...draft, certificateType: e.target.value })
                }
                maxLength={120}
                placeholder="Degree, diploma, certification…"
              />
            </label>
            <label className="text-xs text-gray-600">
              Issuing organisation
              <input
                className="input mt-1"
                value={draft.issuingOrganisation}
                onChange={(e) =>
                  setDraft({ ...draft, issuingOrganisation: e.target.value })
                }
                maxLength={200}
              />
            </label>
            <label className="text-xs text-gray-600">
              Certificate number
              <input
                spellCheck={false}
                className="input mt-1"
                value={draft.certificateNumber}
                onChange={(e) =>
                  setDraft({ ...draft, certificateNumber: e.target.value })
                }
                maxLength={120}
              />
            </label>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={!draft.name.trim() || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save certificate
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setDraft(EMPTY_CERT);
                setAdding(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(certificates ?? []).length === 0 && !adding ? (
        <p className="text-xs text-gray-400">Nothing recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {(certificates ?? []).map((certificate) => (
            <li
              key={certificate.id}
              className="rounded-xl px-3 py-2.5 ring-1 ring-gray-200"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {certificate.name}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {[
                      certificate.certificateType,
                      certificate.issuingOrganisation,
                      certificate.issuedOn,
                      certificate.expiresOn
                        ? `expires ${certificate.expiresOn}`
                        : 'no expiry',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {certificate.certificateNumber && (
                    <p className="mt-0.5 font-mono text-[11px] text-gray-400">
                      {certificate.certificateNumber}
                    </p>
                  )}
                </div>

                {!locked && (
                  <div className="flex shrink-0 items-center gap-2">
                    <label className="btn-ghost cursor-pointer text-xs">
                      <Upload className="h-3.5 w-3.5" />
                      Add scan
                      <input
                        type="file"
                        className="hidden"
                        accept={CERT_ACCEPT}
                        disabled={busyId === certificate.id}
                        onChange={(e) => {
                          void attach(certificate, e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>

                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      disabled={busyId === certificate.id}
                      onClick={() => void remove(certificate)}
                      aria-label="Remove certificate"
                    >
                      {busyId === certificate.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Each scan removable on its own. A front uploaded twice by
                  mistake is a file to delete, not a certificate to re-record. */}
              {certificate.scans.length > 0 ? (
                <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2">
                  {certificate.scans.map((scan) => (
                    <li key={scan.id} className="flex items-center gap-2">
                      <button
                        type="button"
                        className="min-w-0 flex-1 truncate text-left text-xs text-indigo-600 hover:underline"
                        onClick={() => void download(scan)}
                      >
                        {scan.filename}
                      </button>
                      {!locked && (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          disabled={busyId === certificate.id}
                          onClick={() => void removeScan(certificate, scan.id)}
                          aria-label={`Remove ${scan.filename}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-gray-300">No scans attached.</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
